import React from "react";

import {
  WatchInteger,
  WatchNumber,
  WatchText,
  WatchVec2,
} from "@piqnt/runtime-core/react/WatchValue";
import {
  Pair,
  PairKey,
  PairValue,
  Subtree,
  SubtreeBody,
  SubtreeHeader,
} from "@piqnt/runtime-core/react/Subtree";

import { useMiddleware } from "@piqnt/runtime-core/react/ReactMount";
import { SpaceContext } from "../space";

import { getLabel } from "@piqnt/runtime-core/common/Label";
import { getKey, type PlainFixture } from "@piqnt/runtime-space/drawing-model";

import { LiveFixture } from "./LiveFixture";

interface PropsWithFixture {
  fixture: PlainFixture;
}

export const FixtureView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { emit } = useMiddleware<SpaceContext>();

  if (!fixture) return null;

  return (
    <Subtree open>
      <SubtreeHeader>
        <LiveFixture fixture={fixture} />
      </SubtreeHeader>
      <SubtreeBody>
        <Pair>
          <PairKey>label</PairKey>
          <PairValue>
            <WatchText
              editable={true}
              getValue={() => getLabel(fixture)}
              setValue={(v) =>
                emit("fixture-set-props", {
                  key: getKey(fixture),
                  props: { lookupId: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <ShapeView fixture={fixture} />
        <MaterialView fixture={fixture} />
        <FilterView fixture={fixture} />
      </SubtreeBody>
    </Subtree>
  );
};

export const MaterialView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;

  if (!fixture) return null;

  return (
    <Subtree open>
      <SubtreeHeader>material</SubtreeHeader>
      <SubtreeBody>
        <Pair>
          <PairKey>density</PairKey>
          <PairValue>
            <WatchNumber
              editable={editable}
              getValue={() => fixture.density}
              setValue={(v) =>
                emit("fixture-set-props", {
                  fixture: getKey(fixture),
                  props: { density: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>friction</PairKey>
          <PairValue>
            <WatchNumber
              editable={editable}
              getValue={() => fixture.friction}
              setValue={(v) =>
                emit("fixture-set-props", {
                  fixture: getKey(fixture),
                  props: { friction: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>restitution</PairKey>
          <PairValue>
            <WatchNumber
              editable={editable}
              getValue={() => fixture.restitution}
              setValue={(v) =>
                emit("fixture-set-props", {
                  fixture: getKey(fixture),
                  props: { restitution: v },
                })
              }
            />
          </PairValue>
        </Pair>
      </SubtreeBody>
    </Subtree>
  );
};

export const FilterView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;

  if (!fixture) return null;

  return (
    <Subtree>
      <SubtreeHeader>filter</SubtreeHeader>
      <SubtreeBody>
        <Pair>
          <PairKey>group index</PairKey>
          <PairValue>
            <WatchInteger
              editable={editable}
              getValue={() => fixture.filterGroupIndex}
              setValue={(v) => {
                emit("fixture-set-props", {
                  fixture: getKey(fixture),
                  props: { filterGroupIndex: v },
                });
              }}
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>category bits</PairKey>
          <PairValue>
            <WatchInteger
              editable={editable}
              getValue={() => fixture.filterCategoryBits}
              setValue={(v) => {
                emit("fixture-set-props", {
                  fixture: getKey(fixture),
                  props: { filterCategoryBits: v },
                });
              }}
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>mask bits</PairKey>
          <PairValue>
            <WatchInteger
              editable={editable}
              getValue={() => fixture.filterMaskBits}
              setValue={(v) => {
                emit("fixture-set-props", {
                  fixture: getKey(fixture),
                  props: { filterMaskBits: v },
                });
              }}
            />
          </PairValue>
        </Pair>
      </SubtreeBody>
    </Subtree>
  );
};

const ShapeView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const shape = fixture.shape;
  let comp: React.ReactNode;
  if (shape.type === "circle") {
    comp = <CircleView fixture={fixture} />;
  } else if (shape.type === "edge") {
    comp = <EdgeView fixture={fixture} />;
  } else if (shape.type === "box") {
    comp = <BoxView fixture={fixture} />;
  } else if (shape.type === "chain") {
    comp = <ChainView fixture={fixture} />;
  } else if (shape.type === "polygon") {
    comp = <PolygonView fixture={fixture} />;
  }

  return comp;
};

const CircleView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;
  const shape = fixture.shape;
  if (shape.type !== "circle") return;
  return (
    <>
      <Pair>
        <PairKey>center</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => shape.p}
            setValue={(v) =>
              emit("circle-set-center", {
                fixture: getKey(fixture),
                value: v,
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>radius</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => shape.radius}
            setValue={(r) =>
              emit("circle-set-radius", {
                fixture: getKey(fixture),
                value: r,
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EdgeView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;
  const shape = fixture.shape;
  if (shape.type !== "edge") return;

  return (
    <>
      <Pair>
        <PairKey>vertex1</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => shape.vertex1}
            setValue={(v) =>
              emit("edge-set-vertex", {
                fixture: getKey(fixture),
                index: 1,
                value: v,
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>vertex2</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => shape.vertex2}
            setValue={(v) =>
              emit("edge-set-vertex", {
                fixture: getKey(fixture),
                index: 2,
                value: v,
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const BoxView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;
  const shape = fixture.shape;
  if (shape.type !== "box") return;

  return (
    <>
      <Pair key="center">
        <PairKey>center</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => shape.center}
            setValue={(v) =>
              emit("box-set-center", {
                fixture: getKey(fixture),
                value: v,
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair key="half">
        <PairKey>half</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => shape.half}
            setValue={(v) =>
              emit("box-set-half", {
                fixture: getKey(fixture),
                value: v,
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair key="angle">
        <PairKey>angle</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => shape.angle ?? 0}
            setValue={(v) =>
              emit("box-set-angle", {
                fixture: getKey(fixture),
                value: v,
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const ChainView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;
  const shape = fixture.shape;
  if (shape.type !== "chain") return;

  return (
    <Subtree open>
      <SubtreeHeader>vertices</SubtreeHeader>
      <SubtreeBody>
        {shape.vertices?.map((v, i) => (
          <Pair key={i}>
            <PairKey>[{i}]</PairKey>
            <PairValue>
              <WatchVec2
                editable={editable}
                getValue={() => v}
                setValue={(v) =>
                  emit("shape-set-vertex", {
                    fixture: getKey(fixture),
                    index: i,
                    value: v,
                  })
                }
              />
            </PairValue>
          </Pair>
        ))}
      </SubtreeBody>
    </Subtree>
  );
};

const PolygonView: React.FC<PropsWithFixture> = ({ fixture }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;
  const shape = fixture.shape;
  if (shape.type !== "polygon") return;

  return (
    <Subtree open>
      <SubtreeHeader>vertices</SubtreeHeader>
      <SubtreeBody>
        {shape.vertices?.map((v, i) => (
          <Pair key={i}>
            <PairKey>[{i}]</PairKey>
            <PairValue>
              <WatchVec2
                editable={editable}
                getValue={() => v}
                setValue={(v) =>
                  emit("shape-set-vertex", {
                    fixture: getKey(fixture),
                    index: i,
                    value: v,
                  })
                }
              />
            </PairValue>
          </Pair>
        ))}
      </SubtreeBody>
    </Subtree>
  );
};
